import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDropzone } from "react-dropzone";
import SimpleBar from "simplebar-react";
import * as XLSX from "xlsx";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Button, Table, Badge, Spinner,
} from "reactstrap";
import { Alert } from "../ui";
import { isReadOnlyMode } from "../../../helpers/readOnlyMode";

/**
 * 批次匯入共用元件（主計劃 P0-4;WF:整頁三步 stepper + .xlsx）。
 *
 * 三步:①上傳檔案 → ②驗證結果(逐列錯誤/取代提示) → ③已送出審核／完成。
 *
 * 兩種模式,二選一:
 *   - `importApi(rows)`:前端用 SheetJS 解析、依 columns 對標題,送整理過的列(機房/機櫃/人員等 mock 路徑)。
 *   - `uploadApi(file)`:**原始檔直接送後端**（DC Keeper 標準範本 xlsx → DcImport）。
 *     前端不解析、不驗證欄位 —— 後端拿原始標題對欄位,前端多動一下就多一個對不上的機會。
 *     回傳值是 `importResultToView` 的形狀(新增/更新/失敗/逐列原因/警告)。
 *
 *   <BatchImportModal isOpen toggle title columns importApi|uploadApi onDone />
 */

// sheet_to_json 的物件陣列(以標題列為 key)→ 依 columns 的 label 映射成資料 key。
// 模板必填欄標題帶 "*",此處一併對應。
export function mapSheetRows(json, columns) {
  const labelToKey = {};
  columns.forEach((c) => {
    labelToKey[c.label] = c.key;
    labelToKey[`${c.label}*`] = c.key;
  });
  return json.map((obj) => {
    const row = {};
    Object.keys(obj).forEach((h) => {
      const key = labelToKey[String(h).trim()] || h;
      row[key] = obj[h];
    });
    return row;
  });
}

export function parseFile(file, columns) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(new Uint8Array(e.target.result), { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(ws, { defval: "" });
        resolve(mapSheetRows(json, columns));
      } catch (err) {
        // XLSX 解析錯誤是英文技術訊息(如 ZIP/Unsupported file),不能直出給使用者
        // eslint-disable-next-line no-console
        console.error("[BatchImport] 解析失敗", err);
        reject(new Error("檔案無法解析,請確認為 .xlsx/.xls 並套用模板後再上傳。"));
      }
    };
    reader.onerror = () => reject(new Error("檔案讀取失敗,請重新選擇檔案。"));
    reader.readAsArrayBuffer(file);
  });
}

// 第三步的名字跟著「這批會不會進審核」走:有 notice(後端沒有 Import,只驗證)
// 或 UAT 唯讀模式(helpers/readOnlyMode,匯入保留但不審核)時不能寫「已送出審核」——
// 畫面說送審了、實際上沒有任何審核單,驗收時一定被問。
const stepLabels = (noApproval) => ["上傳檔案", "驗證結果", noApproval ? "完成" : "已送出審核"];

const Stepper = ({ step, noApproval }) => (
  <div className="d-flex align-items-center justify-content-center mb-4">
    {stepLabels(noApproval).map((label, i) => {
      const n = i + 1;
      const done = step > n;
      const active = step === n;
      return (
        <React.Fragment key={label}>
          {i > 0 && <div className="flex-grow-1 border-top mx-2" style={{ maxWidth: 60 }} />}
          <div className="text-center" style={{ minWidth: 90 }}>
            <div className={`rounded-circle d-inline-flex align-items-center justify-content-center ${active ? "bg-primary text-white" : done ? "bg-success text-white" : "bg-light text-muted"}`}
              style={{ width: 32, height: 32 }}>
              {done ? <i className="mdi mdi-check" /> : n}
            </div>
            <div className={`small mt-1 ${active ? "fw-semibold" : "text-muted"}`}>{label}</div>
          </div>
        </React.Fragment>
      );
    })}
  </div>
);
Stepper.propTypes = { step: PropTypes.number, noApproval: PropTypes.bool };

// 失敗明細／警告明細共用一張表:列號＋(欄位)＋原因。
// 幾百列的警告(9/9 實傳:503 筆全是「查無管理者」)會把彈窗撐到螢幕外 → 照模板的做法用 SimpleBar
// (Header 通知清單同款)限高、超過就捲;高度收在具名 class .dck-import-lines(2026-09-09 裁示)。
const LineTable = ({ lines, withField, className }) => (
  <SimpleBar className={`dck-import-lines ${className || ""}`.trim()} data-testid="import-lines-scroll">
  <Table size="sm" bordered responsive>
    <thead>
      <tr>
        <th style={{ width: 90 }}>資料列</th>
        {withField && <th style={{ width: 140 }}>欄位</th>}
        <th>原因</th>
      </tr>
    </thead>
    <tbody>
      {lines.map((l, i) => (
        <tr key={i}><td>第 {l.row} 列</td>{withField && <td>{l.field}</td>}<td>{l.reason}</td></tr>
      ))}
    </tbody>
  </Table>
  </SimpleBar>
);
LineTable.propTypes = { lines: PropTypes.array.isRequired, withField: PropTypes.bool, className: PropTypes.string };

const BatchImportModal = ({ isOpen, toggle, title, notice, columns, importApi, uploadApi, onImported, onDone }) => {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [fileName, setFileName] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const uploadMode = typeof uploadApi === "function";
  // 這批不進審核:後端沒有 Import(notice)、原始檔直送後端(後端直接落庫,沒有審核單)、
  // 或 UAT 唯讀模式(匯入保留、不審核)
  const noApproval = !!notice || uploadMode || isReadOnlyMode();

  const reset = () => { setStep(1); setBusy(false); setFileName(""); setResult(null); setError(""); };
  const handleClose = () => { reset(); toggle(); };

  const downloadTemplate = () => {
    const header = columns.map((c) => `${c.label}${c.required ? "*" : ""}`);
    const ws = XLSX.utils.aoa_to_sheet([header]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "匯入");

    // #26.5:有 options 的欄位 → 寫入 Data Validation 下拉選單。
    // SheetJS CE 的 dataValidation 需要把選項寫進隱藏的 "Lists" sheet,
    // 然後用 !dataValidations 引用 range。
    const optCols = columns
      .map((c, i) => (c.options && c.options.length ? { col: i, opts: c.options } : null))
      .filter(Boolean);
    if (optCols.length) {
      // 建 Lists sheet：每欄一組值(縱向排列)
      const listData = [];
      const maxLen = Math.max(...optCols.map((c) => c.opts.length));
      for (let r = 0; r < maxLen; r++) {
        const row = {};
        optCols.forEach((c, ci) => {
          row[`c${ci}`] = r < c.opts.length ? c.opts[r] : undefined;
        });
        listData.push(row);
      }
      const listWs = XLSX.utils.json_to_sheet(listData, { skipHeader: true });
      XLSX.utils.book_append_sheet(wb, listWs, "Lists");
      // 隱藏 Lists sheet(SheetJS CE: Workbook.Sheets metadata)
      if (!wb.Workbook) wb.Workbook = {};
      if (!wb.Workbook.Sheets) wb.Workbook.Sheets = [];
      // 匯入 sheet 已經是 index 0
      wb.Workbook.Sheets[1] = { Hidden: 1 };

      // 加 dataValidation 到匯入 sheet(行 2~1000,Excel 慣例上限)
      if (!ws["!dataValidations"]) ws["!dataValidations"] = [];
      optCols.forEach((c, ci) => {
        const colLetter = XLSX.utils.encode_col(c.col);
        const listColLetter = XLSX.utils.encode_col(ci);
        ws["!dataValidations"].push({
          sqref: `${colLetter}2:${colLetter}1000`,
          type: "list",
          operator: "equal",
          formula1: `Lists!$${listColLetter}$1:$${listColLetter}$${c.opts.length}`,
          showDropDown: true,   // Excel 慣例:true = 顯示下拉箭頭
          showErrorMessage: true,
          errorTitle: "無效的值",
          error: `請從下拉選單中選擇（${c.opts.slice(0, 3).join("/")}${c.opts.length > 3 ? "…" : ""}）`,
        });
      });
    }

    XLSX.writeFile(wb, `${title}_模板.xlsx`);
  };

  const onDrop = useCallback(
    (files) => {
      const file = files[0];
      if (!file || busy) return; // 在途中再丟一個檔案:不排隊、不重送
      setError(""); setFileName(file.name); setBusy(true);
      const run = uploadMode
        ? uploadApi(file)
        : parseFile(file, columns).then((rows) => {
          if (!rows.length) throw new Error("檔案沒有資料列,請套用模板後再上傳。");
          return importApi(rows);
        });
      run
        .then((res) => {
          setResult(res); setStep(2);
          // 原始檔模式在這一步就已經落庫了:列表要在這裡刷新,不能等使用者按到「完成」
          // (在驗證結果頁按 X 關掉,資料一樣已經進去)
          if (onImported) onImported(res);
        })
        .catch((err) => setError(typeof err === "string" ? err : ((err && err.message) || "匯入失敗,請稍後再試。")))
        .finally(() => setBusy(false));
    },
    [columns, importApi, uploadApi, uploadMode, busy, onImported]
  );
  // 拖錯副檔名:dropzone 會靜默丟掉,要自己講
  const onDropRejected = useCallback(() => {
    setError(uploadMode
      ? "只接受 .xlsx 檔案(DC Keeper 標準範本),請重新選擇。"
      : "只接受 .xlsx／.xls／.csv 檔案,請重新選擇。");
  }, [uploadMode]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop, onDropRejected, multiple: false, disabled: busy,
    // 原始檔模式只收 .xlsx(後端 DcImport 標準範本);前端解析模式維持 xlsx/xls/csv
    accept: uploadMode ? {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
    } : {
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [".xlsx"],
      "application/vnd.ms-excel": [".xls"],
      "text/csv": [".csv"],
    },
  });

  // 結果畫面:原始檔模式的逐列原因帶後端的列號(result.rows);前端解析模式是 index+2(result.failed)
  const failedLines = result
    ? (Array.isArray(result.rows) ? result.rows : (result.failed || []).map((f) => ({ row: f.index + 2, field: f.field, reason: f.reason })))
    : [];
  const warningLines = (result && Array.isArray(result.warnings)) ? result.warnings : [];
  // 成功數:原始檔模式信後端的 success_count;前端解析模式是 新增＋取代
  const okCount = result ? (result.successCount != null ? result.successCount : result.success + result.replaced) : 0;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="lg" centered>
      <ModalHeader toggle={handleClose}>{title}</ModalHeader>
      <ModalBody>
        <Stepper step={step} noApproval={noApproval} />

        {step === 1 && (
          <>
            {notice ? <Alert color="warning" className="mb-3">{notice}</Alert> : null}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <span className="text-muted">
                {uploadMode ? "下載模板 → 填寫 → 上傳(.xlsx;整份檔案原樣送出,由系統比對欄位)" : "下載模板 → 填寫 → 上傳(欄位標 * 為必填;支援 .xlsx/.csv)"}
              </span>
              <Button color="light" size="sm" onClick={downloadTemplate}>
                <i className="mdi mdi-download me-1" />下載模板
              </Button>
            </div>
            <div {...getRootProps()} className="border rounded p-5 text-center"
              style={{ borderStyle: "dashed", cursor: "pointer", background: isDragActive ? "#f1f5ff" : "#fafbfe" }}>
              <input {...getInputProps({ "aria-label": "選擇匯入檔案" })} />
              {busy ? (
                <><Spinner size="sm" className="me-2" />處理中…({fileName})</>
              ) : (
                <>
                  <i className="mdi mdi-cloud-upload-outline d-block" style={{ fontSize: 40 }} />
                  {uploadMode ? "將檔案拖曳到此,或點擊選擇(.xlsx)" : "將檔案拖曳到此,或點擊選擇(.xlsx / .csv)"}
                </>
              )}
            </div>
            {error ? <Alert color="danger" className="mt-3 mb-0">{error}</Alert> : null}
          </>
        )}

        {step === 2 && result && (
          <>
            {/* dck-import-badges:模板 badge 的 line-height:1 對中文字會偏上,改為 flex 置中(見 _dck-figma.scss) */}
            <div className="d-flex gap-2 mb-3 dck-import-badges">
              <Badge color="success">新增 {result.success}</Badge>
              <Badge color="warning">{uploadMode ? "更新" : "取代"} {result.replaced}</Badge>
              <Badge color={result.failedCount ? "danger" : "secondary"}>失敗 {result.failedCount}</Badge>
              <span className="text-muted ms-auto">共 {result.total} 筆</span>
            </div>
            {result.replaced > 0 && (
              <Alert color="warning">有 {result.replaced} 筆為既有資料的<strong>{uploadMode ? "更新" : "取代"}</strong>(依唯一鍵比對),{noApproval ? "已更新。" : "已更新並重新送審。"}</Alert>
            )}
            {result.failedCount > 0 ? (
              <>
                <div className="mb-1 text-danger">失敗明細{uploadMode ? "(這些列沒有匯入)" : "(逐欄位)"}:</div>
                <LineTable lines={failedLines} withField={!uploadMode} />
              </>
            ) : (
              <Alert color="success" className={warningLines.length ? "mb-3" : "mb-0"}>{uploadMode ? "全部匯入成功。" : "全部驗證通過。"}</Alert>
            )}
            {warningLines.length > 0 && (
              <>
                <div className="mb-1 text-warning">警告明細(已匯入,請人工確認):</div>
                <LineTable lines={warningLines} className="mb-0" />
              </>
            )}
          </>
        )}

        {step === 3 && result && (
          <div className="text-center py-4">
            <i className="mdi mdi-send-check-outline text-success" style={{ fontSize: 48 }} />
            <h5 className="mt-3 mb-1">{notice ? "驗證完成" : noApproval ? "匯入完成" : "已送出審核"}</h5>
            <p className="text-muted mb-0">
              {notice
                ? `通過 ${okCount} 筆。沒有對應的後端 Import，資料沒有寫進真後端。`
                : noApproval
                  ? `成功匯入 ${okCount} 筆${result.failedCount ? `,${result.failedCount} 筆失敗未匯入` : ""}。`
                  : `成功匯入 ${okCount} 筆,整批已送三關審核。`}
            </p>
          </div>
        )}
      </ModalBody>
      <ModalFooter>
        {step === 1 && <Button color="light" onClick={handleClose}>取消</Button>}
        {step === 2 && (
          <>
            <Button color="light" onClick={reset}>重新上傳</Button>
            <Button color="primary" onClick={() => setStep(3)}>
              {noApproval ? "下一步" : "下一步:送出審核"}
            </Button>
          </>
        )}
        {step === 3 && (
          <>
            <Button color="light" onClick={reset}>再匯入一批</Button>
            <Button color="primary" onClick={() => { if (onDone) onDone(); handleClose(); }}>完成</Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};

BatchImportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  title: PropTypes.string,
  notice: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.string.isRequired, required: PropTypes.bool, options: PropTypes.arrayOf(PropTypes.string) })
  ).isRequired,
  importApi: PropTypes.func,
  uploadApi: PropTypes.func,
  /** 原始檔上傳成功(已落庫)當下呼叫 —— 列表刷新掛這裡,不要掛 onDone */
  onImported: PropTypes.func,
  onDone: PropTypes.func,
};

export default BatchImportModal;
